import React, { ReactNode, useCallback, useEffect, useReducer, useRef } from 'react'
import { OrgProviderContext, orgProviderReducer } from './internal'
import { AccountDto } from '../../dtos/account.dto'
import { useService } from '@pushly/aqe/lib/hooks'
import { AccountService } from '../../services'
import { DomainDto } from '../../dtos/domain'

type OrgProviderProps = {
    orgId: string | number
    org?: AccountDto
    domains?: DomainDto[]
    children: ReactNode
}

export const OrgProvider = (props: OrgProviderProps) => {
    const { orgId: propsOrgId, org, domains, children } = props

    if (org && org.id.toString() !== propsOrgId.toString()) {
        throw new Error('OrgProvider: orgId does not match org.id')
    }

    const orgSvc = useService(AccountService)

    const orgId = org ? org.id : parseInt(propsOrgId.toString(), 10)

    const [state, dispatch] = useReducer(orgProviderReducer, {
        loadingOrg: !org,
        loadingDomains: !domains,
        orgId,
        org,
        domains: [],
    })

    // prevents duplicate load effects without triggering re-renders
    const localOrgLoaderInitializedRef = useRef(false)
    const localDomainsLoaderInitializedRef = useRef(false)

    const loadRemoteOrgDomains = useCallback(async () => {
        const canLoadRemote = !localDomainsLoaderInitializedRef.current || orgId !== state.orgId
        if (!canLoadRemote) return

        localDomainsLoaderInitializedRef.current = true
        if (!state.loadingDomains) dispatch({ type: 'loading:set', domains: true })

        try {
            const res = await orgSvc.fetchAccountDomains(orgId, { pagination: 0 }, true)
            dispatch({ type: 'domains:set', domains: res })
        } finally {
            localDomainsLoaderInitializedRef.current = false
        }

        dispatch({ type: 'loading:set', domains: false })
    }, [orgId, state.orgId, dispatch])

    const loadRemoteOrg = useCallback(async () => {
        const canLoadRemote = !localOrgLoaderInitializedRef.current || orgId !== state.orgId
        if (!canLoadRemote) return

        localOrgLoaderInitializedRef.current = true
        if (!state.loadingOrg) dispatch({ type: 'loading:set', org: true })

        try {
            const res = await orgSvc.fetchById(orgId, {}, true)
            dispatch({ type: 'org:set', org: res })
        } finally {
            localOrgLoaderInitializedRef.current = false
        }

        dispatch({ type: 'loading:set', org: false })
    }, [orgId, state.orgId, dispatch])

    useEffect(() => {
        loadRemoteOrg()
        loadRemoteOrgDomains()
    }, [loadRemoteOrg, loadRemoteOrgDomains])

    const loading = state.loadingOrg || state.loadingDomains

    return (
        <OrgProviderContext.Provider value={{ state, dispatch }}>
            {loading ? null : children}
        </OrgProviderContext.Provider>
    )
}
